import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Table, InputGroup, Form, Spinner, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import Breadcrumb from '../../components/Breadcrumb';
import PageHeader from '../../components/common/PageHeader';
import CreateTicketModal from '../../components/tickets/CreateTicketModal';
import TicketDetailsModal from '../../components/tickets/TicketDetailsModal';
import ConfirmCloseTicketModal from '../../components/tickets/ConfirmCloseTicketModal';
import { API_URL } from '../../config/api';
import './UserTicketsPage.css';

const UserTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketToClose, setTicketToClose] = useState(null);

  const statusLabels = {
    open: { label: 'Aberto', color: 'warning', icon: 'mdi:clock-outline' },
    in_progress: { label: 'Respondido', color: 'info', icon: 'mdi:reply' },
    waiting_user: { label: 'Aguardando Resposta', color: 'secondary', icon: 'mdi:account-clock' },
    resolved: { label: 'Resolvido', color: 'success', icon: 'mdi:check-circle' },
    closed: { label: 'Fechado', color: 'dark', icon: 'mdi:close-circle' }
  };

  const categoryLabels = {
    technical: { label: 'Técnico', icon: 'mdi:wrench', color: '#3B82F6' },
    financial: { label: 'Financeiro', icon: 'mdi:currency-usd', color: '#10B981' },
    administrative: { label: 'Administrativo', icon: 'mdi:account-supervisor', color: '#8B5CF6' },
    general: { label: 'Geral', icon: 'mdi:help-circle', color: '#6B7280' }
  };

  const priorityLabels = {
    low: { label: 'Baixa', color: '#10B981' },
    normal: { label: 'Normal', color: '#3B82F6' },
    high: { label: 'Alta', color: '#F59E0B' },
    urgent: { label: 'Urgente', color: '#EF4444' }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tickets/my-tickets`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao carregar tickets');
      }

      setTickets(data.tickets || []);
    } catch (err) {
      console.error('Erro ao carregar tickets:', err);
      setError(err.message || 'Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketCreated = (newTicket) => {
    setTickets(prev => [newTicket, ...prev]);
    setShowCreateModal(false);
  };

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailsModal(true);
  };

  const handleCloseTicket = (ticket) => {
    setTicketToClose(ticket);
    setShowConfirmCloseModal(true);
  };

  const confirmCloseTicket = async () => {
    if (!ticketToClose) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tickets/${ticketToClose.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'closed' })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao fechar ticket');
      }

      setSuccess('Ticket fechado com sucesso!');
      setShowConfirmCloseModal(false);
      setTicketToClose(null);
      await loadTickets();
    } catch (err) {
      console.error('Erro ao fechar ticket:', err);
      setError(err.message);
    }
  };

  const getFilteredTickets = () => {
    let filtered = tickets;

    // Filtrar por status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(t => t.status === filterStatus);
    }

    // Filtrar por termo de busca
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.id.toString().includes(searchTerm)
      );
    }

    return filtered;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusCount = (status) => {
    if (status === 'all') return tickets.length;
    return tickets.filter(t => t.status === status).length;
  };

  const filteredTickets = getFilteredTickets();

  return (
    <>
      <Breadcrumb 
        title="Tickets de Suporte" 
        items={[
          { label: 'Início', path: '/Dashboard' },
          { label: 'Operações', path: '/tickets' },
          { label: 'Tickets de Suporte', path: '/tickets' }
        ]}
      />

      <PageHeader
        icon="mdi:ticket-account"
        title="Meus Tickets de Suporte"
        subtitle="Acompanhe suas solicitações de suporte e obtenha ajuda da equipe Pagmus"
      >
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          className="d-flex align-items-center gap-2"
        >
          <Icon icon="mdi:plus" />
          Novo Ticket
        </Button>
      </PageHeader>

      <div className="dashboard-main-body">
        <Card className="border-0 shadow-sm">

          <Card.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                <Icon icon="mdi:alert-circle" className="me-2" />
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                <Icon icon="mdi:check-circle" className="me-2" />
                {success}
              </Alert>
            )}

            {/* Filtros */}
            <div className="mb-4">
              <div className="d-flex gap-2 mb-3 flex-wrap">
                <Button
                  variant={filterStatus === 'all' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                  className="d-flex align-items-center gap-2"
                >
                  <Icon icon="mdi:all-inclusive" />
                  Todos
                  {getStatusCount('all') > 0 && (
                    <Badge bg="light" text="dark">{getStatusCount('all')}</Badge>
                  )}
                </Button>
                <Button
                  variant={filterStatus === 'open' ? 'warning' : 'outline-warning'}
                  size="sm"
                  onClick={() => setFilterStatus('open')}
                  className="d-flex align-items-center gap-2"
                >
                  <Icon icon="mdi:clock-outline" />
                  Abertos
                  {getStatusCount('open') > 0 && (
                    <Badge bg="light" text="dark">{getStatusCount('open')}</Badge>
                  )}
                </Button>
                <Button
                  variant={filterStatus === 'in_progress' ? 'info' : 'outline-info'}
                  size="sm"
                  onClick={() => setFilterStatus('in_progress')}
                  className="d-flex align-items-center gap-2"
                >
                  <Icon icon="mdi:progress-clock" />
                  Em Andamento
                  {getStatusCount('in_progress') > 0 && (
                    <Badge bg="light" text="dark">{getStatusCount('in_progress')}</Badge>
                  )}
                </Button>
                <Button
                  variant={filterStatus === 'resolved' ? 'success' : 'outline-success'}
                  size="sm"
                  onClick={() => setFilterStatus('resolved')}
                  className="d-flex align-items-center gap-2"
                >
                  <Icon icon="mdi:check-circle" />
                  Resolvidos
                  {getStatusCount('resolved') > 0 && (
                    <Badge bg="light" text="dark">{getStatusCount('resolved')}</Badge>
                  )}
                </Button>
              </div>

              <InputGroup>
                <InputGroup.Text>
                  <Icon icon="mdi:magnify" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por assunto, descrição ou ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>

            {/* Lista de Tickets */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-3">Carregando tickets...</p>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-5">
                <Icon icon="mdi:inbox-outline" size={64} className="text-muted mb-3" />
                <h5 className="text-muted">
                  {tickets.length === 0 ? 'Nenhum ticket criado' : 'Nenhum ticket encontrado'}
                </h5>
                <p className="text-muted">
                  {tickets.length === 0
                    ? 'Clique em "Novo Ticket" para abrir sua primeira solicitação.'
                    : 'Tente ajustar os filtros de busca.'}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Categoria</th>
                      <th>Assunto</th>
                      <th>Status</th>
                      <th>Prioridade</th>
                      <th>Mensagens</th>
                      <th>Criado em</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td className="fw-bold">#{ticket.id}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Icon
                              icon={categoryLabels[ticket.category]?.icon || 'mdi:help-circle'}
                              size={20}
                              style={{ color: categoryLabels[ticket.category]?.color }}
                            />
                            <span>{categoryLabels[ticket.category]?.label || ticket.category}</span>
                          </div>
                        </td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: '300px' }}>
                            {ticket.subject}
                          </div>
                        </td>
                        <td>
                          <Badge bg={statusLabels[ticket.status]?.color || 'secondary'}>
                            <Icon icon={statusLabels[ticket.status]?.icon} className="me-1" />
                            {statusLabels[ticket.status]?.label || ticket.status}
                          </Badge>
                        </td>
                        <td>
                          <Badge
                            style={{
                              backgroundColor: priorityLabels[ticket.priority]?.color,
                              color: 'white'
                            }}
                          >
                            {priorityLabels[ticket.priority]?.label || ticket.priority}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <Icon icon="mdi:message-text" size={18} className="text-muted" />
                            <span>{ticket.message_count || 0}</span>
                          </div>
                        </td>
                        <td>
                          <small className="text-muted">
                            {formatDate(ticket.created_at)}
                          </small>
                        </td>
                        <td className="text-center">
                          <div className="d-flex gap-1 justify-content-center">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleViewTicket(ticket)}
                              title="Ver detalhes"
                            >
                              <Icon icon="mdi:eye" />
                            </Button>
                            {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleCloseTicket(ticket)}
                                title="Fechar ticket"
                              >
                                <Icon icon="mdi:close" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Modal de Criação */}
        <CreateTicketModal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          onTicketCreated={handleTicketCreated}
        />

        {/* Modal de Detalhes */}
        {selectedTicket && (
          <TicketDetailsModal
            show={showDetailsModal}
            onHide={() => {
              setShowDetailsModal(false);
              setSelectedTicket(null);
            }}
            ticket={selectedTicket}
            onTicketUpdated={loadTickets}
            onCloseTicket={handleCloseTicket}
          />
        )}

        {/* Modal de Confirmação de Fechamento */}
        <ConfirmCloseTicketModal
          show={showConfirmCloseModal}
          onHide={() => {
            setShowConfirmCloseModal(false);
            setTicketToClose(null);
          }}
          onConfirm={confirmCloseTicket}
          ticketId={ticketToClose?.id}
          ticketSubject={ticketToClose?.subject}
        />
      </div>
    </>
  );
};

export default UserTicketsPage;

